import React, { useEffect } from 'react';
import ResponsiveSidebar from '@Components/LeftSidebar';
import { Outlet, useNavigate } from 'react-router-dom';
import { alpha, styled } from '@mui/material/styles';
import { getOrganizationDetails, setOrgCountry } from '@Reducers/OrganizationSetup';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '@Store';
import { organizationSetupRoute } from '@Constants/route';
import { useOrgSetupStatus } from '@Hook/useOrgSetupStatus';
import Header from '@Components/Header';
import SuccessError from '@Components/Modal/SuccessError';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { pickupPinCode } = useSelector((state: RootStateType) => state.getRates.formData);
  const { isBookNow_WithAuth } = useSelector((state: RootStateType) => state.getRates);

  const orgPublicData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const countriesObj = useSelector((state: RootStateType) => state.global.countriesObj);

  const { orgCurrentLevel } = useOrgSetupStatus();

  // redirect create shipment
  useEffect(() => {
    if (pickupPinCode && !isBookNow_WithAuth) {
      navigate('/shipment/create-single');
    }
  }, []);

  // set country
  useEffect(() => {
    if (!orgPublicData?._countryId) return;
    const country = countriesObj[orgPublicData._countryId];
    dispatch(setOrgCountry(country));
  }, [countriesObj, orgPublicData?._countryId, dispatch]);

  // fetch org details
  useEffect(() => {
    dispatch(getOrganizationDetails());
  }, [dispatch]);

  // org level routing
  useEffect(() => {
    if (orgCurrentLevel !== 'shipment-settings') {
      navigate(organizationSetupRoute[orgCurrentLevel]);
    }
  }, [orgCurrentLevel, navigate]);

  if (orgCurrentLevel !== 'shipment-settings') return <Outlet />;

  return (
    <Layout>
      <ResponsiveSidebar />
      <MainSection>
        <Header />
        <Content>
          <Outlet />
        </Content>
      </MainSection>
      <SuccessError />
    </Layout>
  );
};

export default Home;

/* ================= STYLES ================= */

const Layout = styled('section')(({ theme }) => ({
  display: 'flex',
  height: '100vh',
  overflow: 'hidden',
  padding: '10px',
  gap: '10px',
  backgroundColor: theme.palette.mode === 'light' ? '#f5f7fa' : '#0a1929',
}));

const MainSection = styled('div')(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const Content = styled('div')(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: '24px',
  borderRadius: '16px',
  marginTop: '10px',

  // Glassmorphism effect
  background: alpha(theme.palette.background.paper, 0.7),
  backdropFilter: 'blur(20px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  boxShadow: `0 8px 32px 0 ${alpha(theme.palette.common.black, 0.08)}`,
}));
